import utils from './utils.js';

/**
 * This is the core WidgetOptions object. It accepts an excisting JS object
 * and normalizes it to a set of expected settings.
 * Key is, widget configs are unreliable, so this ensures their structure
 */

const TAXONOMY_TEMPLATE = {
	terms: [],
	usePageValues: false,
	matchExact: false,
	matchAll: false,
	excludeAll: false,
	matchAtLeastOne: false
};

// Available Languages
const LANGUAGES = utils.getLanguages();

const FILE_TYPES = [
	{
		id: 'pdf',
		ext: ['pdf'],
		label: 'PDF files',
		icon: 'pdf'
	},
	{
		id: 'ppt',
		ext: ['ppt', 'pptx', 'ppsx'],
		label: 'Power Point files',
		icon: 'ppt'
	},
	{
		id: 'doc',
		ext: ['doc', 'odt', 'docx'],
		label: 'Word docs',
		icon: 'word'
	},
	{
		id: 'xls',
		ext: ['xls', 'xlsx', 'csv'],
		label: 'Spreadsheets',
		icon: 'excel'
	},
	{
		id: 'txt',
		ext: ['txt', 'rtf'],
		label: 'Text files',
		icon: 'txt'
	}
];

// Available filters
const FILTERS = {
	// Language filter option
	language: {
		id: 'cdc_sys_lang',
		name: 'Language',
		label: 'Language',
		value: 'cdc_sys_lang',
	},
	// Date Year filter
	date_y: {
		id: 'date_y',
		name: 'Date by Year',
		label: 'Year',
		value: 'date_y',
		labelFormat: 'YYYY',
		valueFormat: 'YYYY',
		field: 'date',
	},
	// Date Year / Month filter
	date_ym: {
		id: 'date_ym',
		name: 'Date by Year / Month',
		label: 'Month',
		value: 'date_ym',
		labelFormat: 'MMM YYYY',
		valueFormat: 'YYYY/MM',
		field: 'date',
	},
};

// This defines the base structure of WidgetOptions
const CONFIG_TEMPLATE = {

	version: 2,

	// display template
	template: {
		type: 'card',
	},

	subTemplate: {},

	// content types to include
	contentTypes: [
		{
			name: '',
			post_type: '',
		},
	],

	// sites / sources to use
	contentSources: [
		{
			site_id: '',
			folders: [],
			published: 'link',
		},
	],

	// post statuses to query
	status: {
		redirects: false,
		archived: false,
		language: '',
		dates: {},
		dateRange: {
			enabled: false,
			format: 'month',
			length: 1
		},
		fileTypes: [],
		dfeTemplates: [],
	},

	// taxonomies to query
	taxonomies: {
		global: {},
		local: {},
	},

	// fields display in each post
	contentFields: {
		title: {
			display: true,
		},
		url: {
			display: true,
		},
		content: {
			display: true,
		},
		date: {
			// by default, dates don't show
			display: true,
			field: 'cdc_article_date',
		},
		image: {
			display: true,
			hideMobile: false,
		},
		altLanguages: {
			// default not shown
			display: false,
			label: 'Alternative Languages',
		},
		altFormats: {
			// default not shown
			display: false,
			label: 'Alternative Formats',
		},
		relatedContent: {
			// default not shown
			display: false,
			label: 'Related Pages',
		},
	},

	display: {
		initialSort: 'date',
		sortOrder: 'desc',
		// include the current page?
		includePage: false,
		// 0 means no limit
		columns: 1,
		limit: 0,
		pagination: true,
		pageSize: 10,
		// wrapper styles
		header: '',
		displayFilterTags: false,
	},

	// widget form display
	form: {
		// display search field?
		search: false,
		// display counts?
		counts: false,
	},

	// widget form filters
	filters: [
		{
			hierarchical: false,
			id: '',
			label: '',
			name: '',
			taxonomy: '',
			value: '',
		},
	],

	// advanced options
	advanced: {
		customFq: '',
	}
};

// Options available for string / select fields
const OPTIONS = {
	languages: LANGUAGES,
	status: {
		language: Object.assign({'': 'Any Language'}, LANGUAGES),
		fileTypes: FILE_TYPES,
	},

	// use all sites
	siteAll: 'all',
	// use current site
	siteCurrent: 'current',

	contentFields: {
		title: {
			field: {
				post_title: 'Post Title',
				cdc_short_title: 'Short Title',
				cdc_subtitle: 'Subtitle',
			}
		},
		date: {
			field: {
				cdc_article_date: 'Article Date',
				cdc_last_reviewed_date: 'Last Reviewed Date',
				cdc_last_modified_date: 'Last Modified Date',
			},
		},
	},
	template: [
		{ id: 'dfe', name: 'Clean Slate', icon:'ballot-light', type:'dfe', description: 'Use for Collections on DFE Sites' },
		{ id: 'card', name: 'Card', icon: 'square', type: 'card' },
		{ id: 'list', name: 'List', icon: 'ballot-light', type: 'list' },
		{ id: 'related', name: 'Related Pages', icon: 'ballot-light', type: 'related' },
	],
	subTemplate: [
		{ id: 'linklist', type: 'linklist', name: 'Link List', parent: 'dfe' },
		{ id: 'detail', type: 'detail', name: 'Detail', parent: 'dfe' },
		{ id: 'featured', type: 'featured', name: 'Feature', parent: 'dfe' },
	],
	published: {
		link: 'Published to link / live',
		dev: 'Published to dev',
		preview: 'All content',
	},
	filters: FILTERS,
};

// default values for a blank options object
const DEFAULTS = {
	contentTypes: [
		{
			name: 'Page',
			post_type: 'cdc_page',
		},
		{
			name: 'DFE Page',
			post_type: 'cdc_dfe',
		},
	],
	display: {
		initialSort: 'date',
		sortOrder: 'desc',
		// include the current page?
		includePage: false,
		// 0 means no limit
		limit: 0,
		pagination: true,
		pageSize: 10,
		// Wrapper settings
		header: '',
	},
}

class WidgetOptions {
	version = 2;

	// template
	template = {};

	// content Options
	contentTypes = [];

	// content sources
	contentSources = [];

	// content status
	status = {};

	// taxonomies
	taxonomies = {};

	// controls display of base post fields
	contentFields = {};

	// form options
	form = {};

	// Optional, if customer picked Taxonomies to filter by for the template
	filters = [];

	// display options
	display = {};

	/**
	 * WidgetOptions constructor
	 *
	 * @param {object} options
	 */
	constructor(options) {
		let self = this;

		// blank class starts with default values
		if (!options) {
			options = {
				contentTypes: [].concat(DEFAULTS.contentTypes),
				display: Object.assign({}, DEFAULTS.display),
			};
		}

		options = Object.assign({}, options);

		// set version
		this.version = options?.version ? utils.toNumber(options.version) : this.version;

		// set top property objects
		Object.keys(CONFIG_TEMPLATE).forEach((property) => {
			if (utils.isObject(CONFIG_TEMPLATE[property])) {
				self[property] = utils.templateObject(options[property], CONFIG_TEMPLATE[property]);
			} else if (Array.isArray(CONFIG_TEMPLATE[property])) {
				self[property] = utils.templateArray(options[property], CONFIG_TEMPLATE[property][0]);
			} else if ('string' === typeof CONFIG_TEMPLATE[property]) {
				self[property] = utils.cleanString(options[property] || CONFIG_TEMPLATE[property]);
			}
		});

		// Templatetize taxonomy settings
		for (let tax in self.taxonomies.global) {
			let taxObject = self.taxonomies.global[tax];
			if ( Array.isArray(taxObject) ) {
				taxObject = { terms: taxObject };
			}
			self.taxonomies.global[tax] = utils.templateObject(taxObject, TAXONOMY_TEMPLATE)
		}
		for (let tax in self.taxonomies.local) {
			let taxObject = self.taxonomies.local[tax];
			if ( Array.isArray(taxObject) ) {
				taxObject = { terms: taxObject };
			}
			self.taxonomies.local[tax] = utils.templateObject(self.taxonomies.local[tax], TAXONOMY_TEMPLATE)
		}
	}
}

WidgetOptions.options = OPTIONS;

export default WidgetOptions;
